import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  deleteOptionGroupTarget,
  listOptionGroupTargetsForProduct,
  listOptionGroups,
  upsertOptionGroupTarget,
  type OptionGroupTargetRecord,
} from '../../services/options';
import type { ProductSizeRecord } from '../../services/catalog';

interface ProductOptionAssignmentsProps {
  productId: string;
  sizes: ProductSizeRecord[];
}

export default function ProductOptionAssignments({
  productId,
  sizes,
}: ProductOptionAssignmentsProps) {
  const queryClient = useQueryClient();
  const sizeIds = useMemo(
    () => sizes.map((size) => size.id).sort(),
    [sizes],
  );
  const [openSelector, setOpenSelector] = useState<string | null>(null);

  const { data: optionGroups = [], isLoading: groupsLoading } = useQuery({
    queryKey: ["options", "groups"],
    queryFn: listOptionGroups,
  });

  const { data: productTargets = [], isLoading: targetsLoading } = useQuery({
    queryKey: [
      "options",
      "product-targets",
      productId,
      sizeIds.join("-"),
    ],
    queryFn: () => listOptionGroupTargetsForProduct(productId, sizeIds),
    enabled: Boolean(productId),
  });

  const attachMutation = useMutation({
    mutationFn: ({
      groupId,
      productSizeId,
    }: {
      groupId: string;
      productSizeId?: string | null;
    }) =>
      upsertOptionGroupTarget({
        optionGroupId: groupId,
        productId,
        productSizeId: productSizeId ?? null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          "options",
          "product-targets",
          productId,
          sizeIds.join("-"),
        ],
      });
    },
  });

  const detachMutation = useMutation({
    mutationFn: (targetId: string) => deleteOptionGroupTarget(targetId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          "options",
          "product-targets",
          productId,
          sizeIds.join("-"),
        ],
      });
    },
  });

  const sizeTargetsByGroup = useMemo(() => {
    const sizeTargetsMap: Record<string, Record<string, OptionGroupTargetRecord>> = {};

    productTargets.forEach((target) => {
      if (!target.optionGroupId) return;
      if (!sizeTargetsMap[target.optionGroupId]) {
        sizeTargetsMap[target.optionGroupId] = {};
      }
      if (target.productSizeId) {
        sizeTargetsMap[target.optionGroupId][target.productSizeId] = target;
      } else {
        sizes.forEach((size) => {
          sizeTargetsMap[target.optionGroupId][size.id] = target;
        });
      }
    });

    return sizeTargetsMap;
  }, [productTargets, sizes]);

  if (!productId) {
    return null;
  }

  const sortedSizes = [...sizes].sort((a, b) => a.displayOrder - b.displayOrder);
  const allGroups = optionGroups;

  const handleSizeToggle = (groupId: string, sizeId: string, isChecked: boolean) => {
    const existing = sizeTargetsByGroup[groupId]?.[sizeId];
    if (isChecked && !existing) {
      attachMutation.mutate({ groupId, productSizeId: sizeId });
    } else if (!isChecked && existing) {
      detachMutation.mutate(existing.id);
    }
  };

  const renderSelector = (sizeId: string, selectedIds: Set<string>) => {
    if (openSelector !== sizeId) {
      return null;
    }
    const busy = attachMutation.isPending || detachMutation.isPending;
    return (
      <div className="option-selector">
        {allGroups.map((group) => {
          const checked = selectedIds.has(group.id);
          return (
            <label key={group.id} className="option-selector__item">
              <input
                type="checkbox"
                checked={checked}
                disabled={busy}
                onChange={(event) =>
                  handleSizeToggle(group.id, sizeId, event.target.checked)
                }
              />
              <div>
                <strong>{group.name}</strong>
                <p className="helper-text" style={{ margin: 0 }}>
                  {group.selectionType === 'single' ? 'Tek seçim' : 'Çoklu seçim'} ·
                  {group.isRequired ? ' zorunlu' : ' opsiyonel'}
                </p>
              </div>
            </label>
          );
        })}
      </div>
    );
  };

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <p className="card-title">Opsiyon bağlantıları</p>
          <p className="card-subtitle">
            Hangi opsiyon grupları bu ürün ve boyutlarında aktif olacak? Kutucuklar
            değiştiğinde otomatik kaydedilir.
          </p>
        </div>
      </div>
      {groupsLoading || targetsLoading ? (
        <p className="helper-text">Yükleniyor…</p>
      ) : allGroups.length === 0 ? (
        <div className="empty-state">
          <strong>Opsiyon grubu bulunamadı</strong>
          <p>Önce Opsiyonlar sekmesinden en az bir grup oluşturun.</p>
        </div>
      ) : (
        <div className="option-size-list">
          {sortedSizes.map((size) => {
            const selectedIds = new Set(
              Object.entries(sizeTargetsByGroup)
                .filter(([, value]) => value[size.id])
                .map(([groupId]) => groupId),
            );
            const attachedGroups = allGroups.filter((group) => selectedIds.has(group.id));
            return (
              <div key={size.id}>
                <div className="option-size-row">
                  <div>
                    <strong>{size.sizeName}</strong>
                    <p className="helper-text">
                      {attachedGroups.length === 0
                        ? 'Opsiyon eklenmedi'
                        : `${attachedGroups.length} opsiyon bağlı`}
                    </p>
                  </div>
                  <div className="chip-group">
                    {attachedGroups.map((group) => {
                      const target = sizeTargetsByGroup[group.id]?.[size.id];
                      if (!target) return null;
                      return (
                        <span key={target.id} className="chip neutral">
                          {group.name}
                          <button
                            type="button"
                            className="text-button"
                            onClick={() => detachMutation.mutate(target.id)}
                          >
                            Kaldır
                          </button>
                        </span>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    className="text-button add-option-button"
                    onClick={() => setOpenSelector((prev) => (prev === size.id ? null : size.id))}
                  >
                    <span className="add-option-button__icon">+</span>
                    Opsiyon ekle
                  </button>
                </div>
                {renderSelector(size.id, selectedIds)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
